import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

const InventoryUpdateHistory = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const isDateFilterActive = Boolean(year || month);

  const API_URL = process.env.REACT_APP_API_URL;

  const isSameMonthYear = (date, year, month) => {
    if (!date) return false;
    const d = new Date(date);

    if (!year && !month) return true;

    if (year && d.getFullYear().toString() !== year) return false;
    if (
      month &&
      String(d.getMonth() + 1).padStart(2, "0") !== month
    )
      return false;

    return true;
  };

  const normalizeDateForSearch = (date) => {
    if (!date) return "";

    const d = new Date(date);
    if (isNaN(d)) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const monthNum = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    const monthShort = d.toLocaleString("en-GB", { month: "short" }); // Dec
    const monthLong = d.toLocaleString("en-GB", { month: "long" });  // December

    return [
      `${day}/${monthNum}/${year}`,       // 18/12/2025
      `${day}-${monthNum}-${year}`,       // 18-12-2025
      `${day} ${monthShort} ${year}`,     // 18 Dec 2025
      `${day} ${monthLong} ${year}`,      // 18 December 2025
      `${monthShort} ${day} ${year}`,     // Dec 18 2025
    ]
      .join(" ")
      .toLowerCase();
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/inventory-updates`);
      if (res.data.success) {
        setRows(res.data.data);
        setFilteredRows(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching inventory history:", err);
      setSnackbar({
        open: true,
        message: "Error fetching inventory update history",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  // Search filter
  useEffect(() => {
    const q = search.toLowerCase();

    const filtered = rows.filter((r) => {
      const matchesMonthYear = isSameMonthYear(
        r.updatedAt,
        year,
        month
      );

      if (!matchesMonthYear) return false;

      if (!q.trim()) return true;

      const updatedAtStr = normalizeDateForSearch(r.updatedAt);

      return (
        r.sku?.toLowerCase().includes(q) ||
        r.sku_title?.toLowerCase().includes(q) ||
        r.awb_number?.toLowerCase().includes(q) ||
        r.order_number?.toLowerCase().includes(q) ||
        r.processed_by?.toLowerCase().includes(q) ||
        updatedAtStr?.includes(q)
      );
    });

    setFilteredRows(filtered);
  }, [rows, search, month, year]);

  const columns = [
    {
      field: "sn",
      headerName: "S.No",
      width: 70,
      renderCell: (params) => {
        const api = params.api;
        const sortedRowIds = api.getSortedRowIds ? api.getSortedRowIds() : [];
        if (!sortedRowIds.length) return "-";
        const rowIndex = sortedRowIds.indexOf(params.id);
        if (rowIndex === -1) return "-";
        const page = api.state.pagination.paginationModel.page;
        const pageSize = api.state.pagination.paginationModel.pageSize;
        if (isNaN(page) || isNaN(pageSize)) return "-";
        const totalRows = sortedRowIds.length;
        return totalRows - (page * pageSize + rowIndex);
      },
    },
    { field: "marketplaces", headerName: "Marketplace", width: 100 },
    { field: "pickup_partner", headerName: "Pickup Partner", width: 120 },
    { field: "meesho_sku", headerName: "Meesho SKU", width: 180 },
    { field: "sku", headerName: "Base SKU", width: 180 },
    { field: "sku_title", headerName: "Title", width: 280 },
    { field: "effectiveQty", headerName: "Added Qty", width: 100 },
    { field: "return_qty", headerName: "Return Qty", width: 100 },
    { field: "awb_number", headerName: "AWB Number", width: 160 },
    { field: "order_number", headerName: "Order Number", width: 160 },
    { field: "processed_by", headerName: "Processed By", width: 160 },
    { field: "source", headerName: "Source", width: 100 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "-",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Inventory Update History ({filteredRows.length})
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isDateFilterActive && (
              <Tooltip title="Clear date filter">
                <IconButton
                  onClick={() => {
                    setYear("");
                    setMonth("");
                  }}
                  sx={{
                    width: 35,
                    height: 35,
                    backgroundColor: "#c7001eff",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#903341ff", color: "#fff" },
                  }}
                >
                  ✕
                </IconButton>
              </Tooltip>
            )}

            <TextField
              select
              size="small"
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{
                minWidth: 100,
                backgroundColor: month ? "#e3f2fd" : "inherit",
              }}
            >
              <MenuItem value="">All</MenuItem>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem
                  key={i}
                  value={String(i + 1).padStart(2, "0")}
                >
                  {String(i + 1).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{
                minWidth: 100,
                backgroundColor: year ? "#e3f2fd" : "inherit",
              }}
            >
              <MenuItem value="">All</MenuItem>
              {["2024", "2025", "2026"].map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ mr: 1 }} />
                ),
              }}
            />

            <Tooltip title="Refresh">
              <IconButton onClick={fetchHistory} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Data Table */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 5 }}>
            No inventory updates found.
          </Typography>
        ) : (
          <div style={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1976d2",
                  color: "#000000ff",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </div>
        )}

        {/* ✅ Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default InventoryUpdateHistory;
